/**
 * Contexte d'authentification
 * 
 * Gère l'état d'authentification via cookie httpOnly
 * - Au montage, vérifie la session via GET /api/v2/auth/me
 * - Fournit { loading, user, isAuthenticated } aux composants enfants
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

function normalizeAuthUser(raw) {
  if (!raw) return null;
  return {
    ...raw,
    nom: raw.nom ?? raw.medecin_nom ?? null,
    prenom: raw.prenom ?? raw.medecin_prenom ?? null,
  };
}

export function getStoredAuthToken() {
  try {
    const preferSession = sessionStorage.getItem('auth_prefer_session_v1') === '1';
    if (preferSession) {
      return sessionStorage.getItem('token') || localStorage.getItem('token') || '';
    }
    return localStorage.getItem('token') || sessionStorage.getItem('token') || '';
  } catch (_) {
    return '';
  }
}

/**
 * Hook pour accéder au contexte d'authentification
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

/**
 * Provider d'authentification
 * 
 * Vérifie la session au montage et fournit l'état d'authentification
 */
export function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [inactivityTimer, setInactivityTimer] = useState(null);
  
  // Timeout d'inactivité : 20 minutes (1200000 ms)
  const INACTIVITY_TIMEOUT = 20 * 60 * 1000; // 20 minutes

  /**
   * Vérifie la session actuelle via /api/v2/auth/me
   */
  const checkSession = async () => {
    try {
      const token = getStoredAuthToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch('/api/v2/auth/me', {
        method: 'GET',
        headers,
        credentials: 'include', // Important : envoie le cookie httpOnly
      });

      if (res.ok) {
        const data = await res.json();
        if (data.ok && data.user) {
          setUser(normalizeAuthUser(data.user));
          return;
        }
      }
      
      // Session invalide ou expirée
      setUser(null);
    } catch (error) {
      console.error('❌ [AUTH] Erreur vérification session:', error);
      setUser(null);
    }
  };

  /**
   * Fonction de login (appelée après un login réussi)
   */
  const login = async (username, password) => {
    try {
      const res = await fetch('/api/v2/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Important : reçoit le cookie httpOnly
        body: JSON.stringify({ username, password }),
      });

      const data = await res.json();
      
      if (res.ok && data.ok) {
        // Le cookie est maintenant défini, on vérifie la session
        await checkSession();
        return { ok: true, user: normalizeAuthUser(data.user || data.user_public) };
      }
      
      return { ok: false, error: data.error || 'Erreur de connexion' };
    } catch (error) {
      console.error('❌ [AUTH] Erreur login:', error);
      return { ok: false, error: 'Erreur réseau' };
    }
  };

  /**
   * Sauvegarde automatique des données localStorage avant déconnexion
   */
  const saveLocalStorageData = () => {
    try {
      // Sauvegarder toutes les clés localStorage qui contiennent des données de travail
      const keysToSave = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('pr2-gardes:') || key.includes('oph:') || key.includes('dispos:'))) {
          keysToSave.push({ key, value: localStorage.getItem(key) });
        }
      }
      
      // Sauvegarder dans sessionStorage pour récupération après reconnexion
      if (keysToSave.length > 0) {
        sessionStorage.setItem('auto_save_data', JSON.stringify({
          timestamp: new Date().toISOString(),
          data: keysToSave
        }));
        console.log('💾 [AUTH] Données sauvegardées automatiquement avant déconnexion');
      }
    } catch (error) {
      console.error('❌ [AUTH] Erreur sauvegarde automatique:', error);
    }
  };

  /**
   * Fonction de logout avec sauvegarde automatique
   */
  const logout = async (skipSave = false) => {
    const clearStoredAuthSession = () => {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    };

    try {
      if (!skipSave && user) {
        saveLocalStorageData();
      }

      clearStoredAuthSession();

      await fetch('/api/v2/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('❌ [AUTH] Erreur logout:', error);
    } finally {
      clearStoredAuthSession();
      setUser(null);
      // Réinitialiser le timer d'inactivité
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
        setInactivityTimer(null);
      }
    }
  };

  /**
   * Réinitialise le timer d'inactivité
   */
  const resetInactivityTimer = () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
    
    if (user) {
      const timer = setTimeout(() => {
        console.log('⏰ [AUTH] Déconnexion automatique après 20 minutes d\'inactivité');
        logout(false); // Sauvegarder avant déconnexion
      }, INACTIVITY_TIMEOUT);
      
      setInactivityTimer(timer);
    }
  };

  /**
   * Récupère les données sauvegardées automatiquement
   */
  const restoreAutoSavedData = () => {
    try {
      const saved = sessionStorage.getItem('auto_save_data');
      if (saved) {
        const { data, timestamp } = JSON.parse(saved);
        const saveTime = new Date(timestamp);
        const now = new Date();
        
        // Ne restaurer que si la sauvegarde date de moins de 24h
        if (now - saveTime < 24 * 60 * 60 * 1000) {
          data.forEach(({ key, value }) => {
            if (value) {
              localStorage.setItem(key, value);
            }
          });
          console.log('✅ [AUTH] Données restaurées automatiquement');
          sessionStorage.removeItem('auto_save_data');
        } else {
          sessionStorage.removeItem('auto_save_data');
        }
      }
    } catch (error) {
      console.error('❌ [AUTH] Erreur restauration données:', error);
    }
  };

  /**
   * Vérifier la session au montage du composant
   */
  useEffect(() => {
    checkSession().finally(() => {
      setLoading(false);
    });
  }, []);

  /**
   * Gestion de l'inactivité : détecter les événements utilisateur
   */
  useEffect(() => {
    if (!user) {
      // Réinitialiser le timer si pas d'utilisateur
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
        setInactivityTimer(null);
      }
      return;
    }

    // Événements qui indiquent une activité
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    let currentTimer = null;
    
    const handleActivity = () => {
      if (currentTimer) {
        clearTimeout(currentTimer);
      }
      
      currentTimer = setTimeout(() => {
        console.log('⏰ [AUTH] Déconnexion automatique après 20 minutes d\'inactivité');
        saveLocalStorageData();
        logout(true); // skipSave car déjà fait
      }, INACTIVITY_TIMEOUT);
      
      setInactivityTimer(currentTimer);
    };

    // Initialiser le timer
    handleActivity();

    // Ajouter les listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Restaurer les données sauvegardées au login
    restoreAutoSavedData();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      if (currentTimer) {
        clearTimeout(currentTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const value = {
    loading,
    user,
    isAuthenticated: !!user,
    login,
    logout,
    refreshSession: checkSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
